import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ArrowBack, Check, Create, Repeat } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { IGroup, IGroupMonitoringEntry } from '../../shared/interfaces/IGroup';
import { IMonitoring } from '../../shared/interfaces/IMonitoring';

const DEFAULT_COLORS = [
  '#357E8B',
  '#F7B41A',
  '#EA656F',
  '#4CAF50',
  '#2196F3',
  '#9C27B0',
  '#FF5722',
  '#607D8B',
];

interface Props {
  monitoringEntries: IMonitoring[];
  initialData?: Partial<IGroup>;
  onSubmit: (
    data: { name: string; color: string; monitoringEntryIds: string[] },
    options?: { createAnother?: boolean }
  ) => Promise<void>;
  onCancel: () => void;
  onAnotherGroup?: () => void;
}

const GroupWizard: FC<Props> = ({
  monitoringEntries,
  initialData,
  onSubmit,
  onCancel,
  onAnotherGroup,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colorInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(0);
  const isEditing = !!initialData;
  const [name, setName] = useState(initialData?.name ?? '');
  const [color, setColor] = useState(initialData?.color ?? DEFAULT_COLORS[0]);
  const [customColor, setCustomColor] = useState<string | null>(null);
  const [hexInput, setHexInput] = useState((initialData?.color ?? DEFAULT_COLORS[0]).toUpperCase());
  const [selectedEntries, setSelectedEntries] = useState<IMonitoring[]>(
    initialData?.monitoringEntries
      ? monitoringEntries.filter((m) =>
          (initialData.monitoringEntries as IGroupMonitoringEntry[]).some((e) => e.id === m.id)
        )
      : []
  );

  const activeColor = customColor ?? color;

  useEffect(() => {
    setHexInput(activeColor.toUpperCase());
  }, [activeColor]);

  const handleNext = () => setStep((s) => s + 1);
  const handleBack = () => setStep((s) => s - 1);

  const handleColorSelect = (c: string) => {
    setColor(c);
    setCustomColor(null);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomColor(e.target.value);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHexInput(value);

    const normalized = value.startsWith('#') ? value : `#${value}`;
    const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(normalized);

    if (!isValidHex) return;

    const matchedDefaultColor = DEFAULT_COLORS.find(
      (defaultColor) => defaultColor.toLowerCase() === normalized.toLowerCase()
    );

    if (matchedDefaultColor) {
      setColor(matchedDefaultColor);
      setCustomColor(null);
      return;
    }

    setCustomColor(normalized);
  };

  const handleHexInputBlur = () => {
    const normalized = hexInput.startsWith('#') ? hexInput : `#${hexInput}`;
    const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(normalized);

    if (!isValidHex) {
      setHexInput(activeColor.toUpperCase());
      return;
    }

    setHexInput(normalized.toUpperCase());
  };

  const isCustomColorActive = customColor !== null && !DEFAULT_COLORS.includes(activeColor);

  const handleFinish = async (andAnother?: boolean) => {
    await onSubmit(
      {
        name,
        color: activeColor,
        monitoringEntryIds: selectedEntries.map((e) => e.id),
      },
      { createAnother: !!andAnother }
    );

    if (andAnother && onAnotherGroup) {
      onAnotherGroup();
    }
  };

  return (
    <Stepper
      activeStep={step}
      orientation="vertical"
    >
      {/* Step 1: Name & Color */}
      <Step completed={isEditing && step > 0}>
        <StepLabel>{t('group_name_headline')}</StepLabel>
        <StepContent>
          <TextField
            label={t('group_name_placeholder')}
            placeholder={t('group_name_placeholder')}
            variant="standard"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ minWidth: '300px', mb: theme.spacing(2) }}
            autoFocus
          />

          <Typography
            variant="subtitle2"
            sx={{ mt: theme.spacing(2), mb: theme.spacing(1) }}
          >
            {t('group_color_headline')}
          </Typography>

          {/* Default color circles */}
          <Box
            sx={{ display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', mb: theme.spacing(1) }}
          >
            {DEFAULT_COLORS.map((c) => (
              <Tooltip
                key={c}
                title={c}
                arrow
              >
                <Box
                  onClick={() => handleColorSelect(c)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: c,
                    cursor: 'pointer',
                    border:
                      activeColor === c && !customColor
                        ? `3px solid ${theme.palette.text.primary}`
                        : '2px solid transparent',
                    boxSizing: 'border-box',
                    transition: 'border 0.15s',
                  }}
                />
              </Tooltip>
            ))}
          </Box>

          {/* Custom color row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1),
              mb: theme.spacing(2),
            }}
          >
            {/* Placeholder circle – invisible if no custom color chosen, but occupies space so layout doesn't jump */}
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: isCustomColorActive ? customColor! : 'transparent',
                border: isCustomColorActive
                  ? `3px solid ${theme.palette.text.primary}`
                  : '2px solid transparent',
                flexShrink: 0,
                boxSizing: 'border-box',
                transition: 'background-color 0.15s, border 0.15s',
              }}
            />
            <TextField
              value={hexInput}
              onChange={handleHexInputChange}
              onBlur={handleHexInputBlur}
              variant="outlined"
              size="small"
              placeholder="#RRGGBB"
              inputProps={{ maxLength: 7 }}
              sx={{
                width: 100,
                '& .MuiOutlinedInput-root': {
                  height: 32,
                },
              }}
            />
            {/* Styled label that opens color input */}
            <Box
              component="label"
              htmlFor="group-custom-color-input"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                cursor: 'pointer',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: `${theme.shape.borderRadius}px`,
                px: theme.spacing(1.5),
                py: theme.spacing(0.5),
                fontSize: '0.875rem',
                userSelect: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Create fontSize="small" />
              {t('more_colors')}
            </Box>
            <input
              id="group-custom-color-input"
              ref={colorInputRef}
              type="color"
              value={customColor ?? color}
              onChange={handleCustomColorChange}
              style={{
                position: 'absolute',
                opacity: 0,
                pointerEvents: 'none',
                width: 0,
                height: 0,
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              disabled={name.trim() === ''}
              onClick={handleNext}
              sx={{ mt: 1, mr: 1 }}
            >
              {t('next')}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={onCancel}
              sx={{ mt: 1, mr: 1 }}
            >
              {t('cancel')}
            </Button>
          </Box>
        </StepContent>
      </Step>

      {/* Step 2: Monitoring selection */}
      <Step completed={isEditing && step > 1}>
        <StepLabel>{t('nav_monitoring')}</StepLabel>
        <StepContent>
          <Autocomplete
            multiple
            disableCloseOnSelect={true}
            options={monitoringEntries}
            value={selectedEntries}
            onChange={(_, newValue) => setSelectedEntries(newValue)}
            getOptionLabel={(option) => `${option.label}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('nav_platforms')}
                placeholder={t('monitoring')}
                variant="standard"
                sx={{ minWidth: '300px' }}
              />
            )}
            sx={{ mb: theme.spacing(2) }}
          />
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ mt: 1, mr: 1 }}
            >
              {t('next')}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleBack}
              sx={{ mt: 1, mr: 1 }}
            >
              {t('back')}
            </Button>
          </Box>
        </StepContent>
      </Step>

      {/* Step 3: Finish */}
      <Step completed={isEditing && step > 2}>
        <StepLabel>{t('finish_configuration')}</StepLabel>
        <StepContent>
          <Typography
            variant="body2"
            sx={{ mb: theme.spacing(2) }}
          >
            {t('group_guide_info_text')}
          </Typography>
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
            <Button
              variant="contained"
              color="warning"
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{ color: theme.palette.text.primary }}
            >
              {t('back')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Check />}
              onClick={() => handleFinish(false)}
            >
              {t('finish')}
            </Button>
            {onAnotherGroup && (
              <Button
                variant="outlined"
                startIcon={<Repeat />}
                onClick={() => handleFinish(true)}
              >
                {t('another_group')}
              </Button>
            )}
          </Box>
        </StepContent>
      </Step>
    </Stepper>
  );
};

export default GroupWizard;
